import React, { useEffect, useState } from "react";
import { FiChevronDown, FiPhone, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./RequestCallModal.css";
import { createCallRequest, getCallRequestServices } from "../../../api/callRequests";

const RequestCallModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    message: "",
    serviceOptionId: "",
  });

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    const loadServices = async () => {
      setLoadingServices(true);
      setErrorMessage("");
      try {
        const data = await getCallRequestServices();
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setServices(list);
        setForm((prev) => ({
          ...prev,
          serviceOptionId: prev.serviceOptionId || String(list[0]?.id || ""),
        }));
      } catch (error) {
        if (!isMounted) return;
        setServices([]);
        setErrorMessage(error?.response?.data?.message || t("requestCall.errors.loadServicesFailed"));
      } finally {
        if (isMounted) setLoadingServices(false);
      }
    };
    loadServices();
    return () => {
      isMounted = false;
    };
  }, [isOpen, t]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    if (!String(form.name || "").trim()) {
      setErrorMessage(t("requestCall.errors.nameRequired"));
      return;
    }
    if (!String(form.phoneNumber || "").trim()) {
      setErrorMessage(t("requestCall.errors.phoneRequired"));
      return;
    }
    if (!String(form.serviceOptionId || "").trim()) {
      setErrorMessage(t("requestCall.errors.serviceRequired"));
      return;
    }

    setSubmitting(true);
    try {
      await createCallRequest({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim() || null,
        phoneNumber: String(form.phoneNumber || "").trim(),
        message: String(form.message || "").trim() || null,
        serviceOptionId: Number(form.serviceOptionId),
      });
      setSuccessMessage(t("requestCall.success"));
      setForm((prev) => ({
        ...prev,
        name: "",
        email: "",
        phoneNumber: "",
        message: "",
      }));
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || t("requestCall.errors.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="requestCallModalOverlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="requestCallModal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("requestCall.dialogAriaLabel")}
      >
        <button
          type="button"
          className="requestCallModalClose"
          onClick={onClose}
          aria-label={t("requestCall.closeAriaLabel")}
        >
          <FiX />
        </button>

        <div className="requestCallModalForm">
          <h2>{t("requestCall.title")}</h2>
          <p>{t("requestCall.description")}</p>

          <input
            type="text"
            placeholder={t("requestCall.namePlaceholder")}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <input
            type="email"
            placeholder={t("requestCall.emailPlaceholder")}
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
          <input
            type="text"
            placeholder={t("requestCall.phonePlaceholder")}
            value={form.phoneNumber}
            onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
          />
          <textarea
            placeholder={t("requestCall.messagePlaceholder")}
            rows="4"
            value={form.message}
            onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
          />

          <div className="requestCallModalSelect">
            <select
              value={form.serviceOptionId}
              onChange={(e) => setForm((p) => ({ ...p, serviceOptionId: e.target.value }))}
              disabled={loadingServices || !services.length}
              aria-label={t("requestCall.serviceAriaLabel")}
            >
              {services.length ? (
                services.map((service) => (
                  <option key={service.id} value={String(service.id)}>
                    {service.name}
                  </option>
                ))
              ) : (
                <option value="">
                  {loadingServices ? t("requestCall.loadingServices") : t("requestCall.noServices")}
                </option>
              )}
            </select>
            <FiChevronDown />
          </div>

          {errorMessage ? <p className="requestCallModalMessage requestCallModalMessageError">{errorMessage}</p> : null}
          {successMessage ? <p className="requestCallModalMessage requestCallModalMessageSuccess">{successMessage}</p> : null}

          <button type="button" className="requestCallModalSubmit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? t("requestCall.submitting") : t("requestCall.submit")}
          </button>

          <div className="requestCallModalPhoneRow">
            <FiPhone />
            <span>
              {t("requestCall.orCallUs")} <strong>+964 750 202 0066</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestCallModal;
